import { Base } from "ui/Scripts/source/framework/base";
import { IdColorTitle, IIdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { IdTitle, IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { IdTitleNode, IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { INumberTitle } from "ui/Scripts/source/models/common/numberTitle";
import { EventStateFilter } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";

export interface IEventPageFilterLists {
    units: IIdTitleNode[];
    states: IIdTitle[];
    urgencyClasses: IdColorTitle[];
    partners: IIdTitle[];
    creators: IIdTitle[];
    handlers: IIdTitle[];
    observers: IIdTitle[];
    eventTypes: IIdTitleNode[];
    eventTypeBs: IIdTitleNode[];
    eventTypeCs: IIdTitleNode[];
    dynamicTimeFilters: IIdTitle[];
}

export const EventTypeNotSetId = "00000000-0000-0000-0000-000000000001";

export interface IEventPageFilterStateWithoutDates {
    selectedUnitIds: string[];
    selectedStateIds: string[];
    selectedUrgencyClassIds: string[];
    selectedEventTypeIds: string[];
    selectedEventTypeBIds: string[];
    selectedEventTypeCIds: string[];
    selectedPartnerCompanyIds: string[];
    selectedCreateUserCodes: string[];
    selectedHandlerUserCodes: string[];
    selectedObservers: string[];
    selectedDynamicTimeFilterId: string;
}

export interface IEventPageFilterState extends IEventPageFilterStateWithoutDates {
    selectedStartDate: number;
    selectedEndDate: number
}

export interface IEventGeneralFilter {
    startDate: number;
    endDate: number;
    unitIds: string[];
    states: number[];
    urgencyClassIds: string[];
    eventTypeIds: string[];
    eventTypeBIds: string[];
    eventTypeCIds: string[];
    partnerCompanyIds: string[];
    createUserCodes: string[];
    handlerUserCodes: string[];
    observers: string[];
    dynamicTimeFilterId: number;
}

export interface IEventGeneralFilterChange extends IEventGeneralFilter {
    notChanged: boolean;
}

export const getEventGeneralFilterFromFilterState = (state: IEventPageFilterState): IEventGeneralFilter => {
    return {
        startDate: state?.selectedStartDate ? state.selectedStartDate : null,
        endDate: state?.selectedEndDate ? state.selectedEndDate : null,
        unitIds: state?.selectedUnitIds ? state.selectedUnitIds.filter(i => i !== Base.emptyGuid) : [],
        states: state?.selectedStateIds ? state.selectedStateIds.map(i => i.toInteger(true)).filter(i => i > 0) : [],
        urgencyClassIds: state?.selectedUrgencyClassIds ? state.selectedUrgencyClassIds.filter(i => i !== Base.emptyGuid) : [],
        eventTypeIds: state?.selectedEventTypeIds ? state.selectedEventTypeIds.filter(i => i !== Base.emptyGuid) : [],
        eventTypeBIds: state?.selectedEventTypeBIds ? state.selectedEventTypeBIds.filter(i => i !== Base.emptyGuid) : [],
        eventTypeCIds: state?.selectedEventTypeCIds ? state.selectedEventTypeCIds.filter(i => i !== Base.emptyGuid) : [],
        partnerCompanyIds: state?.selectedPartnerCompanyIds ? state.selectedPartnerCompanyIds.filter(i => i !== Base.emptyGuid) : [],
        createUserCodes: state?.selectedCreateUserCodes ? state.selectedCreateUserCodes.filter(i => !!i) : [],
        handlerUserCodes: state?.selectedHandlerUserCodes ? state.selectedHandlerUserCodes.filter(i => !!i) : [],
        observers: state?.selectedObservers ? state.selectedObservers.filter(i => i !== Base.emptyGuid) : [],
        dynamicTimeFilterId: state?.selectedDynamicTimeFilterId ? parseInt(state.selectedDynamicTimeFilterId, 10) : 0
    };
};

export const getFilterStatePropsFromEventGeneralFilter = (generalFilter: any): IEventPageFilterState => {
    const states = generalFilter?.states?.filter(i => !Base.isNullOrUndefined(i)) ?? [];
    const urgencyClassIds = generalFilter?.urgencyClassIds?.filter(i => Base.isValidGuid(i)) ?? [];
    const eventTypeIds = generalFilter?.eventTypeIds?.filter(i => Base.isValidGuid(i)) ?? [];
    const eventTypeBIds = generalFilter?.eventTypeBIds?.filter(i => Base.isValidGuid(i)) ?? [];
    const eventTypeCIds = generalFilter?.eventTypeCIds?.filter(i => Base.isValidGuid(i)) ?? [];
    const partnerCompanyIds = generalFilter?.partnerCompanyIds?.filter(i => Base.isValidGuid(i)) ?? [];
    const createUserCodes = generalFilter?.createUserCodes?.filter(i => !Base.isNullOrUndefined(i)) ?? [];
    const handlerUserCodes = generalFilter?.handlerUserCodes?.filter(i => !Base.isNullOrUndefined(i)) ?? [];
    const observers = generalFilter?.observers?.filter(i => !Base.isNullOrUndefined(i)) ?? [];
    return {
        selectedStartDate: generalFilter?.startDate
            ? generalFilter.startDate
            : 0,
        selectedEndDate: generalFilter?.endDate
            ? generalFilter.endDate
            : 0,
        selectedUnitIds: generalFilter?.unitIds?.length
            ? generalFilter.unitIds
            : [Base.emptyGuid],
        selectedStateIds: states.length
            ? states.map(i => i.toString(10))
            : [EventStateFilter.All.toString(10)],
        selectedUrgencyClassIds: urgencyClassIds.length
            ? urgencyClassIds
            : [Base.emptyGuid],
        selectedEventTypeIds: eventTypeIds.length
            ? eventTypeIds
            : [Base.emptyGuid],
        selectedEventTypeBIds: eventTypeBIds.length
            ? eventTypeBIds
            : [Base.emptyGuid],
        selectedEventTypeCIds: eventTypeCIds.length
            ? eventTypeCIds
            : [Base.emptyGuid],
        selectedPartnerCompanyIds: partnerCompanyIds.length
            ? partnerCompanyIds
            : [Base.emptyGuid],
        selectedCreateUserCodes: createUserCodes.length
            ? createUserCodes
            : [""],
        selectedHandlerUserCodes: handlerUserCodes.length
            ? handlerUserCodes
            : [""],
        selectedObservers: observers.length
            ? observers
            : [Base.emptyGuid],
        selectedDynamicTimeFilterId: !Base.isNullOrUndefined(generalFilter?.dynamicTimeFilterId)
            ? generalFilter.dynamicTimeFilterId.toString(10)
            : "0"
    };
};

export const getEventGeneralFilterChange = (prevProps: IEventPageFilterState, props: IEventPageFilterState): IEventGeneralFilterChange => {
    const prevValues = getEventGeneralFilterFromFilterState(prevProps);
    const values = getEventGeneralFilterFromFilterState(props);
    return {
        ...values,
        notChanged: (prevValues.startDate ?? -99).toString(10) === (values.startDate ?? -99).toString(10) &&
            (prevValues.endDate ?? -99).toString(10) === (values.endDate ?? -99).toString(10) &&
            Base.equalStrArrays(prevValues.unitIds, values.unitIds) &&
            Base.equalStrArrays(prevValues.states.map(i => i.toString(10)), values.states.map(i => i.toString(10))) &&
            Base.equalStrArrays(prevValues.urgencyClassIds, values.urgencyClassIds) &&
            Base.equalStrArrays(prevValues.eventTypeIds, values.eventTypeIds) &&
            Base.equalStrArrays(prevValues.eventTypeBIds, values.eventTypeBIds) &&
            Base.equalStrArrays(prevValues.eventTypeCIds, values.eventTypeCIds) &&
            Base.equalStrArrays(prevValues.partnerCompanyIds, values.partnerCompanyIds) &&
            Base.equalStrArrays(prevValues.createUserCodes, values.createUserCodes) &&
            Base.equalStrArrays(prevValues.handlerUserCodes, values.handlerUserCodes) &&
            Base.equalStrArrays(prevValues.observers, values.observers) &&
            prevValues.dynamicTimeFilterId.toString(10) === values.dynamicTimeFilterId.toString(10)
    };
};

export const cloneEventGeneralFilter = (state: IEventGeneralFilter): IEventGeneralFilter => {
    return {
        startDate: state?.startDate,
        endDate: state?.endDate,
        unitIds: state?.unitIds ?? [],
        states: state?.states ?? [],
        urgencyClassIds: state?.urgencyClassIds ?? [],
        eventTypeIds: state?.eventTypeIds ?? [],
        eventTypeBIds: state?.eventTypeBIds ?? [],
        eventTypeCIds: state?.eventTypeCIds ?? [],
        partnerCompanyIds: state?.partnerCompanyIds ?? [],
        createUserCodes: state?.createUserCodes ?? [],
        handlerUserCodes: state?.handlerUserCodes ?? [],
        observers: state?.observers ?? [],
        dynamicTimeFilterId: state?.dynamicTimeFilterId ?? 0
    };
};

export const cloneEventPageFilterState = (state: IEventPageFilterState): IEventPageFilterState => {
    return {
        selectedStartDate: state?.selectedStartDate,
        selectedEndDate: state?.selectedEndDate,
        selectedUnitIds: state?.selectedUnitIds ?? [Base.emptyGuid],
        selectedStateIds: state?.selectedStateIds ?? [EventStateFilter.All.toString(10)],
        selectedUrgencyClassIds: state?.selectedUrgencyClassIds ?? [Base.emptyGuid],
        selectedEventTypeIds: state?.selectedEventTypeIds ?? [Base.emptyGuid],
        selectedEventTypeBIds: state?.selectedEventTypeBIds ?? [Base.emptyGuid],
        selectedEventTypeCIds: state?.selectedEventTypeCIds ?? [Base.emptyGuid],
        selectedPartnerCompanyIds: state?.selectedPartnerCompanyIds ?? [Base.emptyGuid],
        selectedCreateUserCodes: state?.selectedCreateUserCodes ?? [""],
        selectedHandlerUserCodes: state?.selectedHandlerUserCodes ?? [""],
        selectedObservers: state?.selectedObservers ?? [Base.emptyGuid],
        selectedDynamicTimeFilterId: state?.selectedDynamicTimeFilterId ?? "0"
    };
};
     
export const getEventPageDynamicTimeFilters = (includeManualInput: boolean): IIdTitle[] => {
    const result: IIdTitle[] = [];
    if (includeManualInput) {
        result.push(IdTitle.createIdTitle("0", "'" + Translations.SpecifedTimeFrame + "'"));
    }
    result.push(IdTitle.createIdTitle("1", Translations.Last30Days));
    result.push(IdTitle.createIdTitle("2", Translations.Last120Days));
    result.push(IdTitle.createIdTitle("3", Translations.Last180Days));
    result.push(IdTitle.createIdTitle("4", Translations.LastYear));
    result.push(IdTitle.createIdTitle("7", Translations.CurrentCalendarMonth));
    result.push(IdTitle.createIdTitle("8", Translations.CurrentCalendarYear));
    result.push(IdTitle.createIdTitle("5", Translations.PreviousCalendarMonth));
    result.push(IdTitle.createIdTitle("6", Translations.PreviousCalendarYear));
    return result;
};

export const getEventFiltersFromNumberTitle = (items: INumberTitle[]): IIdTitle[] => {
    const result: IIdTitle[] = [];
    result.push(IdTitle.createIdTitle(EventStateFilter.All.toString(10), "'" + Translations.All + "'"));
    return result.concat(items.map(i => IdTitle.createIdTitle(i.number.toString(10), i.title)));
};

export const getEventIdTitleNodeFilters = (items: IIdTitleNode[]): IIdTitleNode[] => {
    const result: IIdTitleNode[] = [];
    result.push(IdTitleNode.createIdTitleNode(Base.emptyGuid, "'" + Translations.All + "'", []));
    return result.concat(items);
};

export const getEventFiltersFromIdColorTitle = (items: IIdColorTitle[]): IIdColorTitle[] => {
    const result: IIdColorTitle[] = [];
    result.push(IdColorTitle.createIdColorTitle(Base.emptyGuid, "", "'" + Translations.All + "'"));
    return result.concat(items.map(i => IdColorTitle.createIdColorTitle(i.id, i.color, i.title)));
};

export const getEventTypeFilters = (items: IIdTitleNode[]): IIdTitleNode[] => {
    const result: IIdTitleNode[] = [];
    result.push(IdTitleNode.createIdTitleNode(Base.emptyGuid, "'" + Translations.All + "'", []));
    result.push(IdTitleNode.createIdTitleNode(EventTypeNotSetId, "'" + Translations.NotSet + "'", []));
    return result.concat(items);
};